/**
 * Сортируемый заголовок таблицы.
 *
 * design: derived, not documented — reference/app-kit.md §3: сортируемый
 * заголовок это `<button>` внутри `<th>`, `aria-sort` стоит на `th`,
 * отсортирована одновременно **одна** колонка, указатель — треугольник в
 * `--ink-3`, а не стрелка-глиф (voice.md запрещает `▲ ▼` как подмену).
 *
 * Вынесено отдельно, потому что таблиц в кабинете много, а половинчатая
 * сортировка без `aria-sort` — это таблица, в которой человек с экранным
 * диктором не знает, что он вообще видит.
 */

import type { ReactNode } from 'react';

import { collator } from '@shared/src/intl';

export type Direction = 'asc' | 'desc';

export interface Sort<K extends string> {
  key: K;
  direction: Direction;
}

/** Переключение: та же колонка меняет направление, другая начинает с возрастания. */
export function nextSort<K extends string>(current: Sort<K>, key: K): Sort<K> {
  if (current.key !== key) return { key, direction: 'asc' };
  return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

export function SortHeader<K extends string>({
  column,
  sort,
  onSort,
  numeric,
  children,
}: {
  column: K;
  sort: Sort<K>;
  onSort: (key: K) => void;
  /** Числовые колонки выравниваются вправо: цифры сравнивают по разрядам. */
  numeric?: boolean;
  children: ReactNode;
}) {
  const active = sort.key === column;
  return (
    <th
      scope="col"
      className={numeric ? 'tnum-col' : undefined}
      aria-sort={
        active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      <button type="button" className="th-sort" onClick={() => onSort(column)}>
        {children}
        <span
          className={active ? `caret ${sort.direction}` : 'caret'}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

/**
 * Сравнение для сортировки. Пустое значение всегда внизу независимо от
 * направления: «не измерено» — это не наименьшее число, это отсутствие числа.
 */
export function compare(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
  direction: Direction,
): number {
  const emptyA = a === null || a === undefined || a === '';
  const emptyB = b === null || b === undefined || b === '';
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;

  const sign = direction === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * sign;
  // Алфавит активной локали, а не 'ru' буквой: сортировка колонки — то, что
  // человек читает как порядок, и на арабском он другой.
  return collator().compare(String(a), String(b)) * sign;
}
